import { BiCheck, BiRightArrowAlt } from "react-icons/bi";
import {
  BillingCycle,
  PLANS,
  PlanKey,
  PlanNeeds,
  formatLimit,
  formatPrice,
} from "./plans";

export const EVENT_STEPS = [1, 3, 5, 10, 20, 35, 50, 75, 100];
export const ITEM_STEPS = [10, 25, 50, 100, 250, 500, 1000, 2500];

interface PlanFinderProps {
  needs: PlanNeeds;
  onChange: (needs: PlanNeeds) => void;
  recommended: PlanKey;
  billingCycle: BillingCycle;
}

const stepLabel = (steps: number[], value: number) =>
  value >= steps[steps.length - 1]
    ? `${value.toLocaleString()}+`
    : value.toLocaleString();

const StepSlider = ({
  label,
  hint,
  steps,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  steps: number[];
  value: number;
  onChange: (value: number) => void;
}) => (
  <label className="block">
    <div className="flex items-baseline justify-between">
      <span className="text-sm font-medium text-slate-200">{label}</span>
      <span className="text-2xl font-bold tabular-nums text-white">
        {stepLabel(steps, value)}
      </span>
    </div>
    <input
      type="range"
      min={0}
      max={steps.length - 1}
      step={1}
      value={Math.max(0, steps.indexOf(value))}
      onChange={(e) => onChange(steps[Number(e.target.value)])}
      className="mt-3 w-full cursor-pointer accent-blue-500"
    />
    <span className="mt-1 block text-xs text-slate-500">{hint}</span>
  </label>
);

const Toggle = ({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className={`flex w-full items-center justify-between gap-4 rounded-2xl border px-4 py-3 text-left text-sm transition ${
      checked
        ? "border-blue-500/50 bg-blue-500/10 text-white"
        : "border-white/10 bg-white/[0.03] text-slate-300 hover:border-white/20"
    }`}
  >
    {label}
    <span
      className={`relative h-6 w-11 flex-shrink-0 rounded-full transition ${
        checked ? "bg-blue-500" : "bg-white/15"
      }`}
    >
      <span
        className={`absolute top-1 h-4 w-4 rounded-full bg-white transition-all ${
          checked ? "left-6" : "left-1"
        }`}
      />
    </span>
  </button>
);

const PlanFinder: React.FC<PlanFinderProps> = ({
  needs,
  onChange,
  recommended,
  billingCycle,
}) => {
  const plan = PLANS[recommended];
  const price =
    billingCycle === "monthly" ? plan.monthlyPrice : plan.annualPrice;

  const reasons = [
    `Room for ${formatLimit(plan.limits.events).toLowerCase()} active events (you need ${stepLabel(EVENT_STEPS, needs.events)})`,
    `Room for ${formatLimit(plan.limits.items).toLowerCase()} items (you have ${stepLabel(ITEM_STEPS, needs.items)})`,
  ];
  if (needs.hasStaff) reasons.push("Employees can sell at your booth");
  if (needs.needsPools) reasons.push("Separate inventory pools per show");

  const scrollToPlan = () =>
    document
      .getElementById(`plan-${recommended}`)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });

  return (
    <div className="mx-auto mt-24 max-w-5xl">
      <div className="mb-8 text-center">
        <p className="text-sm font-semibold uppercase tracking-[0.22em] text-blue-400">
          Plan finder
        </p>
        <h2 className="mt-3 text-3xl font-semibold tracking-tight text-white md:text-4xl">
          Not sure which plan fits?
        </h2>
        <p className="mt-3 text-slate-400">
          Tell us about your booth and we’ll point you to the right plan.
        </p>
      </div>

      <div className="grid overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-sm md:grid-cols-[1.4fr_1fr]">
        <div className="space-y-8 p-6 md:p-10">
          <StepSlider
            label="Events on your calendar"
            hint="Archived events don’t count toward the limit."
            steps={EVENT_STEPS}
            value={needs.events}
            onChange={(events) => onChange({ ...needs, events })}
          />
          <StepSlider
            label="Items in your inventory"
            hint="Each product you sell counts as one item."
            steps={ITEM_STEPS}
            value={needs.items}
            onChange={(items) => onChange({ ...needs, items })}
          />
          <div className="space-y-3">
            <Toggle
              label="Staff sell at my booth"
              checked={needs.hasStaff}
              onChange={(hasStaff) => onChange({ ...needs, hasStaff })}
            />
            <Toggle
              label="I keep separate stock for different shows"
              checked={needs.needsPools}
              onChange={(needsPools) => onChange({ ...needs, needsPools })}
            />
          </div>
        </div>

        <div className="relative flex flex-col justify-between border-t border-white/10 bg-gradient-to-br from-blue-600/20 via-violet-600/10 to-transparent p-6 md:border-l md:border-t-0 md:p-10">
          <div aria-live="polite">
            <p className="text-sm font-medium text-emerald-300">Your fit</p>
            <p className="mt-2 text-5xl font-bold tracking-tight text-white">
              {plan.title}
            </p>
            <p className="mt-2 text-slate-300">
              {price === 0
                ? "Free forever"
                : `${formatPrice(price)} ${
                    billingCycle === "monthly" ? "a month" : "a year"
                  }`}
            </p>
            <ul className="mt-6 space-y-3">
              {reasons.map((reason) => (
                <li key={reason} className="flex gap-3 text-sm text-slate-300">
                  <BiCheck
                    size={18}
                    className="mt-0.5 flex-shrink-0 text-emerald-400"
                  />
                  {reason}
                </li>
              ))}
            </ul>
          </div>
          <button
            type="button"
            onClick={scrollToPlan}
            className="mt-8 inline-flex items-center gap-2 self-start rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-slate-900 transition hover:bg-slate-200"
          >
            See the {plan.title} plan
            <BiRightArrowAlt size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlanFinder;
